import { DataManagementConfig } from '@shared/components/data-management';
import { AddSubjectUserDialog } from '../components/AddSubjectUserDialog';
import { SubjectUserTableData, SubjectUserFormData, RemoveSubjectUserParams } from '../types';
import { useSubjectUsers, useInviteSubjectUser } from '../hooks/useSubjectUsers';
import { RoleChip } from '@shared/components/RoleChip';
import { userInvitationApi } from '@features/user-invitation/api/userInvitationApi';
import { notificationService } from '@shared/components/notifications';

export const createSubjectUsersConfig = (
  tenantId: string,
  domainId: string,
  subjectId: string,
  removeSubjectUserMutation?: {
    mutateAsync: (params: RemoveSubjectUserParams) => Promise<void>;
  },
  _confirmFn?: (options: {
    title: string;
    message: string;
    confirmText: string;
    confirmButtonColor: 'warning' | 'error';
  }) => Promise<boolean>
): DataManagementConfig<SubjectUserTableData, SubjectUserFormData, SubjectUserFormData> => ({
  hooks: {
    useQuery: () => useSubjectUsers(tenantId, subjectId),
    useCreate: () => useInviteSubjectUser(tenantId, domainId, subjectId),
    useDelete: removeSubjectUserMutation ? () => removeSubjectUserMutation : undefined,
  },

  table: {
    title: 'Subject Users',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      {
        key: 'role',
        label: 'Role',
        render: (role) =>
          role && typeof role === 'string' ? <RoleChip role={role} size="small" /> : null,
      },
      {
        key: 'assignedAt',
        label: 'Assigned At',
        render: (date) =>
          date && typeof date === 'string' ? new Date(date).toLocaleDateString() : null,
      },
      {
        key: 'status',
        label: 'Status',
        render: (status) => {
          const color =
            status === 'active' ? '#4caf50' : status === 'invited' ? '#ff9800' : '#f44336';
          return (
            <span
              style={{
                color,
                fontWeight: 'bold',
                textTransform: 'capitalize',
              }}
            >
              {status}
            </span>
          );
        },
      },
    ],
    emptyMessage: 'No users found for this subject',
    errorMessage: 'Failed to load subject users. Please try refreshing the page.',
  },

  actions: {
    create: {
      label: 'Add Subject User',
      dialog: (props) => (
        <AddSubjectUserDialog
          {...props}
          tenantId={tenantId}
          domainId={domainId}
          subjectId={subjectId}
        />
      ),
    },
    delete: removeSubjectUserMutation
      ? {
          label: 'Remove',
          confirmTitle: 'Remove Subject User',
          confirmMessage: (user) => `Remove ${user.name || user.email} from this subject?`,
          transformParams: (user) => ({
            userId: user.userId,
            tenantId: user.tenantId,
            userRoleId: user.userRoleId,
            userName: user.name || user.email,
            role: user.role,
          }),
        }
      : undefined,
    custom: [
      {
        label: 'Resend Invite',
        onClick: async (user) => {
          try {
            const response = await userInvitationApi.resendInvitation(user.email);
            if (response.data?.success) {
              notificationService.success(`Invitation resent to ${user.email}`);
            } else {
              notificationService.error(
                response.data?.errorMessage || response.message || 'Failed to resend invitation'
              );
            }
          } catch (error) {
            console.error('Failed to resend invitation:', error);
            notificationService.error('Failed to resend invitation');
          }
        },
        color: 'primary',
        variant: 'text',
        condition: (user) => user.status === 'invited',
      },
    ],
  },

  features: {
    search: true,
    pagination: true,
  },
});
